import { useEffect, useMemo, useRef, useState } from 'react';
import Sheet from './Sheet';
import Icon from './Icon';
import { resizeImages } from '../lib/image';
import { MEAL_LABELS, MEAL_SHORT, MEAL_TYPES, foldText, grams, kcal, mealTypeForNow } from '../lib/format';
import { today } from '../lib/dates';
import { useApp } from '../lib/AppContext';
import { adPerRequest, useAiCredits } from '../lib/credits';
import { hasSources, recompute, totals } from '../lib/foodTable';
import IngredientBreakdown from './IngredientBreakdown';
import { SourceLine } from './DataSource';

export const PRIMARY = 'btn btn-primary h-[54px] w-full rounded-2xl border-0 text-base font-bold';
const CHIP = 'btn h-11 min-h-11 rounded-full border-0 bg-base-200 px-4 text-sm font-semibold';
const LABEL = 'text-[15px] font-semibold';
const FIELD = 'input h-11 w-full rounded-[14px] border-line text-base';
const MAX_WORKOUT_PHOTOS = 3;

function useRecent(path) {
  const { api } = useApp();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(path).then(setRows).catch(() => {}); }, [api, path]);
  return rows;
}

function Thumbs({ files, onRemove, size = 'size-24' }) {
  const urls = useMemo(() => files.map((file) => URL.createObjectURL(file)), [files]);
  useEffect(() => () => urls.forEach((url) => URL.revokeObjectURL(url)), [urls]);
  return urls.map((url, i) => (
    <div key={url} className={`relative shrink-0 ${size}`}>
      <img src={url} alt={`Ảnh ${i + 1}`} className="size-full rounded-2xl object-cover" />
      {onRemove && (
        <button type="button" aria-label={`Bỏ ảnh ${i + 1}`} onClick={() => onRemove(i)}
          className="absolute -right-2 -top-2 grid size-8 place-items-center rounded-full bg-neutral text-neutral-content">
          <Icon name="x" className="size-4" />
        </button>
      )}
    </div>
  ));
}

function FileTile({ icon, label, capture, onFiles }) {
  return (
    <label className="flex size-24 shrink-0 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-2xl border border-line bg-base-200/60 text-sm font-semibold focus-within:outline-2 focus-within:outline-primary">
      <Icon name={icon} className="size-6" />
      {label}
      <input type="file" accept="image/*" multiple={!capture} capture={capture} className="sr-only"
        onChange={(e) => { onFiles(Array.from(e.target.files)); e.target.value = ''; }} />
    </label>
  );
}

function ErrorText({ children }) {
  return children ? <p role="alert" className="text-sm font-medium text-error">{children}</p> : null;
}

// "✦ Dùng 1 lượt AI · hôm nay còn N lượt", just above the AI button.
export function CreditsNote({ text }) {
  return text ? (
    <p className="flex items-center justify-center gap-1.5 text-xs font-semibold text-brand-text">
      <Icon name="sparkles" className="size-3.5" /> {text}
    </p>
  ) : null;
}

// ---------- Ghi bữa ăn ----------

const NUMBERS = [
  { key: 'calories', label: 'kcal' },
  { key: 'protein_g', label: 'Đạm (g)' },
  { key: 'carbs_g', label: 'Tinh bột (g)' },
  { key: 'fat_g', label: 'Chất béo (g)' },
];

function MealForm({ sheet, onSaved }) {
  const { api } = useApp();
  const credits = useAiCredits(1, { label: 'Tự nhập số liệu', onClick: () => enterByHand() });
  const recent = useRecent('/meals/recent');
  const [mealType, setMealType] = useState(sheet.mealType || mealTypeForNow());
  const [picked, setPicked] = useState(!!sheet.mealType);
  const [files, setFiles] = useState([]);
  const [description, setDescription] = useState('');
  const [step, setStep] = useState('form');
  const [estimate, setEstimate] = useState(null);
  const [values, setValues] = useState(null);
  // Totals typed by hand win over the ingredients (the meal is saved as "tự nhập").
  const [typed, setTyped] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const run = async (label, fn) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  // A corrected weight: the ingredient and, unless totals were typed, the totals follow.
  const setGrams = (index, grams) => {
    const items = estimate.items.map((item, i) => (i === index ? recompute(item, grams) : item));
    setEstimate({ ...estimate, items });
    if (!typed) setValues(Object.fromEntries(NUMBERS.map(({ key }) => [key, String(totals(items)[key])])));
  };

  const analyze = () => {
    if (credits.blocked(analyze)) return;
    if (!description.trim() && files.length === 0) {
      setError('Hãy chụp ảnh hoặc mô tả món đã ăn.');
      return;
    }
    run('analyze', async () => {
      const form = new FormData();
      form.append('meal_type', mealType);
      form.append('description', description);
      (await resizeImages(files)).forEach((file) => form.append('photos[]', file));
      const result = await api.post('/meals/analyze', form, { idempotencyKey: credits.key() });
      credits.used();
      setEstimate(result);
      setTyped(false);
      setValues(Object.fromEntries(NUMBERS.map(({ key }) => [key, String(result[key] ?? '')])));
      setStep('review');
    });
  };

  // When the AI can't help (no network, unreadable photo), the numbers can still be typed in.
  const enterByHand = () => {
    setEstimate(null);
    setValues(Object.fromEntries(NUMBERS.map(({ key }) => [key, ''])));
    setError(null);
    setStep('review');
  };

  const save = () => run('save', async () => {
    const edited = !estimate || typed;
    const form = new FormData();
    form.append('meal_type', mealType);
    form.append('description', description);
    NUMBERS.forEach(({ key }) => values[key] !== '' && form.append(key, values[key]));
    if (estimate) {
      form.append('items', JSON.stringify(estimate.items || []));
      form.append('ai_summary', estimate.ai_summary || '');
    }
    form.append('analysis_status', edited ? 'manual' : 'analyzed');
    (await resizeImages(files)).forEach((file) => form.append('photos[]', file));
    onSaved(await api.post(`/days/${sheet.date}/meals`, form));
  });

  const relog = (meal) => run(`relog-${meal.id}`, async () => {
    onSaved(await api.post(`/days/${sheet.date}/meals`, { copy_of: meal.id, meal_type: mealType }));
  });

  if (step === 'review') {
    const valid = Number(values.calories) >= 0 && values.calories !== '';
    return (
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-muted">{MEAL_LABELS[mealType]}</span>
          {estimate && (
            <span className="rounded-full bg-base-200 px-3 py-1 text-xs font-semibold text-muted">
              {hasSources(estimate.items) && !typed ? 'AI nhận diện' : 'AI ước tính'}
            </span>
          )}
        </div>

        {(files.length > 0 || description) && (
          <div className="flex items-center gap-3">
            {files.length > 0 && <div className="flex gap-2"><Thumbs files={files.slice(0, 2)} size="size-16" /></div>}
            {description && <p className="line-clamp-3 text-sm">{description}</p>}
          </div>
        )}

        {hasSources(estimate?.items) && !typed && <IngredientBreakdown items={estimate.items} onGrams={setGrams} live />}
        {estimate?.items?.length > 0 && !hasSources(estimate.items) && (
          <ul className="divide-y divide-line rounded-[20px] border border-line px-4">
            {estimate.items.map((item, i) => (
              <li key={i} className="flex justify-between gap-3 py-3 text-sm">
                <span>
                  <span className="font-semibold">{item.name}</span>
                  <span className="block text-muted">{item.portion}</span>
                </span>
                <span className="tabular shrink-0 text-right">
                  {kcal(item.calories)} kcal
                  <span className="block text-muted">{grams(item.protein_g)} đạm</span>
                </span>
              </li>
            ))}
          </ul>
        )}
        {estimate?.ai_summary && <p className="text-sm text-muted">{estimate.ai_summary}</p>}

        <div className="grid grid-cols-2 gap-3">
          {NUMBERS.map(({ key, label }) => (
            <label key={key} className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold">{label}</span>
              <input type="number" inputMode="decimal" min="0" step="any" className={`${FIELD} tabular`}
                value={values[key]} onChange={(e) => { setTyped(true); setValues((v) => ({ ...v, [key]: e.target.value })); }} />
            </label>
          ))}
        </div>
        {/* Typed totals aren't from the tables: no source line then. */}
        {!typed && <SourceLine items={estimate?.items} className="-mt-3" />}
        <p className="text-xs text-muted">
          {hasSources(estimate?.items) && !typed
            ? 'Sửa khối lượng từng nguyên liệu ở trên để tính lại, không tốn lượt. Gõ thẳng tổng số sẽ thành "tự nhập".'
            : 'Sửa số nếu cần. Bạn vẫn có thể sửa lại sau khi lưu.'}
        </p>

        <ErrorText>{error}</ErrorText>
        <div className="flex flex-col gap-2">
          <button type="button" className={PRIMARY} disabled={!!busy || !valid} onClick={save}>
            {busy === 'save' && <span className="loading loading-spinner loading-sm" />} Lưu bữa ăn
          </button>
          <button type="button" className="btn btn-ghost h-11 rounded-2xl" disabled={!!busy} onClick={() => setStep('form')}>
            Chụp lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <div role="radiogroup" aria-label="Bữa" className="grid grid-cols-4 gap-1 rounded-[16px] bg-base-200 p-1">
          {MEAL_TYPES.map((type) => (
            <button key={type} type="button" role="radio" aria-checked={mealType === type}
              onClick={() => { setMealType(type); setPicked(true); }}
              className={`h-11 rounded-[12px] text-sm font-semibold transition ${mealType === type ? 'bg-neutral text-neutral-content' : 'text-base-content'}`}>
              {MEAL_SHORT[type]}
            </button>
          ))}
        </div>
        {!picked && <p className="text-xs text-muted">Tự chọn theo giờ hiện tại, bấm để đổi</p>}
      </div>

      <div className="flex flex-col gap-2">
        <span className={LABEL}>Ảnh món ăn</span>
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 pt-2">
          <Thumbs files={files} onRemove={(i) => setFiles(files.filter((_, j) => j !== i))} />
          <FileTile icon="camera" label="Chụp ảnh" capture="environment" onFiles={(f) => setFiles([...files, ...f])} />
          <FileTile icon="photo" label="Thư viện" onFiles={(f) => setFiles([...files, ...f])} />
        </div>
      </div>

      <label className="flex flex-col gap-2">
        <span className={LABEL}>Mô tả thêm <span className="font-normal text-muted">(giúp AI tính chính xác hơn)</span></span>
        <textarea className="textarea min-h-24 w-full rounded-[14px] border-line text-base" value={description}
          placeholder="VD: Cơm gà, khoảng 150g gà, 1 chén cơm" onChange={(e) => setDescription(e.target.value)} />
      </label>

      {recent.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className={LABEL}>Ăn lại món gần đây</span>
          <div className="flex flex-wrap gap-2">
            {recent.map((meal) => (
              <button key={meal.id} type="button" className={CHIP} disabled={!!busy} onClick={() => relog(meal)}
                aria-label={`Ghi lại ${meal.title}, ${kcal(meal.calories)} kcal`}>
                {busy === `relog-${meal.id}` && <span className="loading loading-spinner loading-xs" />}
                {meal.title} · {kcal(meal.calories)}
              </button>
            ))}
          </div>
        </div>
      )}

      <ErrorText>{error}</ErrorText>
      <div className="flex flex-col items-center gap-2">
        <CreditsNote text={credits.note} />
        <button type="button" className={PRIMARY} disabled={!!busy} onClick={analyze}>
          {busy === 'analyze' ? <><span className="loading loading-spinner loading-sm" /> AI đang phân tích…</> : 'Phân tích bằng AI'}
        </button>
        <p className="text-xs text-muted">Bạn sẽ xem lại kcal và đạm trước khi lưu</p>
        {error && (
          <button type="button" className="btn btn-ghost btn-sm h-11 text-brand-text" onClick={enterByHand}>Tự nhập số liệu</button>
        )}
      </div>
    </div>
  );
}

export function MealSheet({ sheet, onClose, onSaved }) {
  return (
    <Sheet open={!!sheet} onClose={onClose} title="Ghi bữa ăn">
      {sheet && <MealForm sheet={sheet} onSaved={onSaved} />}
    </Sheet>
  );
}

// ---------- Ghi tập luyện ----------

const SMALL_LABEL = 'text-sm font-semibold';
const OPTIONAL = <span className="font-normal text-muted">(không bắt buộc)</span>;

// A number field with its unit inside on the right ("45 phút", "500 kcal"); `approx` adds "≈" for the AI's estimate.
export function UnitField({ label, unit, value, onChange, placeholder, approx = false, highlight = false, inputRef }) {
  return (
    <label className="flex min-w-0 flex-1 flex-col gap-1.5">
      <span className={SMALL_LABEL}>{label}</span>
      <span className={`flex h-11 items-center gap-1 rounded-[14px] border px-3 ${highlight ? 'border-2 border-[#17804f] bg-[#e3f1e9]/50' : 'border-line'}`}>
        {approx && <span className="font-semibold text-[#17804f]">≈</span>}
        <input ref={inputRef} type="number" inputMode="numeric" min="1" className="tabular w-full min-w-0 bg-transparent text-base outline-none"
          value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
        <span className="shrink-0 text-sm text-muted">{unit}</span>
      </span>
    </label>
  );
}

// "Cách AI tính": the figures the AI used and its result.
export function AiExplanation({ text }) {
  return text ? (
    <div className="rounded-[14px] border border-line p-3 text-sm">
      <p className="font-semibold">Cách AI tính</p>
      <p className="whitespace-pre-line text-muted">{text}</p>
    </div>
  ) : null;
}

const dayMonth = (iso) => `${Number(iso.slice(8, 10))}/${Number(iso.slice(5, 7))}`;

let entryKey = 0;
const newEntry = (name = '', files = []) => ({
  key: (entryKey += 1), name, duration: '', calories: '', notes: '', showNotes: false, files, kind: 'other',
  // The earlier workout it was filled in from ("Đã điền theo buổi 22/10"), and the AI's answer while its number is unchanged.
  filledFrom: null, ai: null,
});
const kcalOf = (e) => (Number(e.calories) > 0 ? Number(e.calories) : null);
const minutesOf = (e) => (Number(e.duration) > 0 ? Number(e.duration) : 0);
const named = (e) => e.name.trim() !== '';
const blank = (e) => !named(e) && !e.duration && !e.calories && !e.notes.trim() && e.files.length === 0;

// While typing "Bạn tập gì?": up to 3 of the person's earlier workouts that match, accents and case aside, then
// "Ghi mới: …" to keep what was typed. Picking one fills everything in, without a credit.
function Suggestions({ query, history, onPick, onNew }) {
  const q = foldText(query);
  const matches = q ? history.filter((a) => foldText(a.name).includes(q)).slice(0, 3) : [];
  if (!matches.length) return null;
  const keepFocus = (e) => e.preventDefault();
  return (
    <div className="overflow-hidden rounded-[14px] border border-line bg-base-100 shadow-md">
      <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
        Đã tập trước đây <span className="font-normal normal-case">· không tốn lượt</span>
      </p>
      {matches.map((a) => (
        <button key={a.id} type="button" onMouseDown={keepFocus} onClick={() => onPick(a)}
          className="flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left active:bg-base-200">
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[15px] font-semibold">{a.name}</span>
            <span className="block truncate text-xs text-muted">
              {[a.duration_minutes && `${a.duration_minutes} phút`, a.calories && `${kcal(a.calories)} kcal`, `lần gần nhất ${dayMonth(a.date)}`].filter(Boolean).join(' · ')}
            </span>
          </span>
          <span className="shrink-0 text-sm font-semibold text-brand-text">Dùng lại</span>
        </button>
      ))}
      <button type="button" onMouseDown={keepFocus} onClick={onNew}
        className="block min-h-11 w-full truncate border-t border-line px-3 py-2.5 text-left text-sm active:bg-base-200">
        Ghi mới: <b>“{query.trim()}”</b>
      </button>
    </div>
  );
}

// A workout already filled in, while another one is open: one line, the pencil opens it again.
function CollapsedEntry({ entry: e, onOpen }) {
  const meta = minutesOf(e) ? `${minutesOf(e)} phút` : '';
  return (
    <div className="flex items-center gap-3 rounded-[14px] bg-base-200/70 py-1 pl-3 pr-1">
      <span className="min-w-0 flex-1 py-1.5">
        <span className="block truncate text-[15px] font-semibold">{e.name.trim() || 'Chưa đặt tên'}</span>
        {meta && <span className="block text-xs text-muted">{meta}</span>}
      </span>
      {kcalOf(e)
        ? <span className="tabular shrink-0 text-sm font-bold text-brand-text">+{kcal(kcalOf(e))} kcal</span>
        : <span className="shrink-0 text-xs font-semibold text-warning">Chưa có kcal</span>}
      <button type="button" className="btn btn-circle btn-ghost size-11 shrink-0" aria-label={`Sửa ${e.name.trim() || 'môn này'}`} onClick={onOpen}>
        <Icon name="pencil" className="size-4" />
      </button>
    </div>
  );
}

// Logged by hand first, several workouts in one go: "Bạn tập gì?" is the name (typing it suggests earlier
// workouts, which fill everything in without a credit), and time, duration, kcal, a note and photos are optional.
// Only one workout is open at a time; the others fold into a line. Without kcal it still saves (still a workout
// day). Photos are only saved with it, never sent to the AI. Only "Để AI ước tính kcal" spends a credit: one for
// every workout still without kcal, in one request, and the result is shown to review before saving ("Xem lại
// trước khi lưu", with how the AI worked the number out). Each workout is saved as its own, all in one request.
function WorkoutForm({ sheet, onSaved, onReview }) {
  const { api, membership } = useApp();
  const nameRef = useRef(null);
  const durationRef = useRef(null);
  const credits = useAiCredits(1);
  const history = useRecent('/activities/recent');
  const [entries, setEntries] = useState(() => [newEntry(sheet.description || '', (sheet.files || []).slice(0, MAX_WORKOUT_PHOTOS))]);
  const [openKey, setOpenKey] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const current = entries.find((e) => e.key === openKey) || entries[0];
  const multi = entries.length > 1;
  const filled = entries.filter((e) => !blank(e));
  const ready = filled.length > 0 && filled.every(named);
  const toEstimate = filled.filter((e) => named(e) && !kcalOf(e));
  const totalKcal = filled.reduce((sum, e) => sum + (kcalOf(e) || 0), 0);
  const totalMinutes = filled.reduce((sum, e) => sum + minutesOf(e), 0);

  const update = (key, patch) => {
    setError(null);
    setEntries((es) => es.map((e) => (e.key === key ? { ...e, ...patch } : e)));
  };
  const set = (field) => (value) => {
    const patch = { [field]: value };
    if (field === 'name') patch.filledFrom = null;
    if (field === 'calories' && current.ai && String(current.ai.calories) !== String(value)) patch.ai = null;
    update(current.key, patch);
  };
  // Opening another workout folds the open one; an untouched one is dropped.
  const open = (key, extra = []) => {
    setError(null);
    setSuggesting(false);
    setEntries((es) => [...es.filter((e) => e.key === key || !(e.key === current.key && blank(e))), ...extra]);
    setOpenKey(key);
  };
  const add = () => {
    const entry = newEntry();
    open(entry.key, [entry]);
    setTimeout(() => nameRef.current?.focus());
  };
  const remove = (key) => {
    const i = entries.findIndex((e) => e.key === key);
    const rest = entries.filter((e) => e.key !== key);
    setError(null);
    setEntries(rest);
    setOpenKey(rest[Math.max(0, i - 1)].key);
  };
  const pick = (a) => {
    setSuggesting(false);
    update(current.key, {
      name: a.name, kind: a.kind, duration: a.duration_minutes ? String(a.duration_minutes) : '',
      calories: a.calories ? String(a.calories) : '', filledFrom: a.date, ai: null,
    });
  };

  const run = async (label, fn) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const estimate = () => {
    if (credits.blocked(estimate)) return;
    const noDuration = toEstimate.find((e) => !minutesOf(e));
    if (noDuration) {
      if (noDuration.key !== current.key) open(noDuration.key);
      setError(`Hãy nhập thời lượng cho “${noDuration.name.trim()}” để AI ước tính.`);
      setTimeout(() => durationRef.current?.focus());
      return;
    }
    const targets = toEstimate;
    run('analyze', async () => {
      const result = await api.post('/activities/scan', {
        sessions: targets.map((e) => ({ name: e.name.trim(), duration_minutes: minutesOf(e), notes: e.notes.trim() })),
      }, { idempotencyKey: credits.key() });
      if (!result.activities.length) throw new Error(result.note || 'AI không ước tính được. Hãy ghi rõ hơn hoặc nhập kcal tay.');
      credits.used();
      const found = new Map(result.activities.map((a) => [targets[a.session]?.key, a]));
      setEntries((es) => es.map((e) => {
        const a = found.get(e.key);
        return a ? { ...e, kind: a.kind, calories: String(a.calories), ai: { calories: a.calories, explanation: a.explanation } } : e;
      }));
      setOpenKey(targets.find((e) => found.has(e.key))?.key ?? current.key);
      setReviewed(true);
      onReview(true);
    });
  };

  const save = () => run('save', async () => {
    const form = new FormData();
    for (const [i, e] of filled.entries()) {
      const key = `activities[${i}]`;
      form.append(`${key}[name]`, e.name.trim());
      form.append(`${key}[kind]`, e.kind);
      form.append(`${key}[calories]`, kcalOf(e) ?? '');
      form.append(`${key}[duration_minutes]`, minutesOf(e) || '');
      form.append(`${key}[notes]`, e.notes.trim());
      form.append(`${key}[ai_estimated]`, e.ai ? 'true' : 'false');
      (await resizeImages(e.files)).forEach((file) => form.append(`${key}[photos][]`, file));
    }
    onSaved(await api.post(`/days/${sheet.date}/activities`, form));
  });

  const credit = membership?.credits;
  const leftText = credit && credit.left != null ? `${credit.left}/${credit.limit}` : null;
  const outOfCredits = !!membership && credit.left != null && credits.left < 1 && !adPerRequest(membership);
  const showAi = filled.length === 0 || toEstimate.length > 0 || filled.some((e) => !named(e));
  let footer = 'Nhập tên buổi tập để AI có dữ liệu ước tính';
  if (outOfCredits) footer = 'Đã hết lượt AI hôm nay · vẫn lưu được, nhập kcal tay nếu có';
  else if (reviewed) footer = 'Bấm vào số kcal để sửa nếu đồng hồ báo khác';
  else if (toEstimate.length && multi) footer = `1 lượt ước tính cho mọi môn chưa có kcal (${toEstimate.map((e) => e.name.trim()).join(', ')})${leftText ? ` · còn ${leftText}` : ''}`;
  else if (toEstimate.length) footer = leftText ? `Còn ${leftText} lượt hôm nay · chỉ trừ khi AI trả kết quả` : 'Chỉ trừ lượt khi AI trả kết quả';

  let saveLabel = filled.length > 1 ? `Lưu ${filled.length} môn` : 'Lưu buổi tập';
  if (totalKcal) saveLabel += ` · +${kcal(totalKcal)} kcal`;

  const e = current;
  const showSuggestions = suggesting && !e.filledFrom && named(e);
  return (
    <div className="flex flex-col gap-3">
      {reviewed && (
        <p className="flex gap-2 rounded-[14px] bg-[#e3f1e9] p-3 text-sm text-[#15261f]">
          <Icon name="sparkles" className="mt-0.5 size-4 shrink-0 text-[#17804f]" />
          <span>AI đã ước tính kcal từ tên, thời lượng và ghi chú của bạn. <b>Đã dùng 1 lượt{leftText ? ` · còn ${leftText} hôm nay` : ''}.</b></span>
        </p>
      )}

      {entries.map((entry, i) => (entry.key !== e.key ? <CollapsedEntry key={entry.key} entry={entry} onOpen={() => open(entry.key)} /> : (
        <div key={entry.key} className={multi ? 'flex flex-col gap-3 rounded-[16px] border-2 border-[#17804f]/50 p-3' : 'flex flex-col gap-3'}>
          {multi && (
            <div className="-my-2 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wide text-brand-text">Môn {i + 1}</span>
              <button type="button" className="min-h-11 px-2 text-sm font-semibold text-error" onClick={() => remove(e.key)}>Xoá</button>
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`workout-${e.key}`} className={SMALL_LABEL}>Bạn tập gì?</label>
            <input id={`workout-${e.key}`} ref={nameRef} className={FIELD} maxLength={80} autoComplete="off"
              placeholder="VD: Tập chân, Chạy bộ 5 km" value={e.name}
              onChange={(ev) => { set('name')(ev.target.value); setSuggesting(true); }} onBlur={() => setTimeout(() => setSuggesting(false), 200)} />
            {showSuggestions && <Suggestions query={e.name} history={history} onPick={pick} onNew={() => setSuggesting(false)} />}
            {e.filledFrom && (
              <p className="flex items-center gap-1 text-xs font-semibold text-brand-text">
                <Icon name="check" className="size-3.5" /> Đã điền theo buổi {dayMonth(e.filledFrom)} <span className="font-normal text-muted">· sửa nếu hôm nay khác</span>
              </p>
            )}
            {e.showNotes || e.notes ? (
              <input className={FIELD} maxLength={500} aria-label="Ghi chú" placeholder="Ghi chú, VD: 5 km, nhịp tim TB 150"
                value={e.notes} onChange={(ev) => set('notes')(ev.target.value)} />
            ) : (
              <button type="button" className="-my-2 min-h-11 self-start text-sm font-semibold text-muted" onClick={() => update(e.key, { showNotes: true })}>
                + Thêm ghi chú
              </button>
            )}
          </div>

          <div className="flex gap-3">
            <UnitField label="Thời lượng" unit="phút" placeholder="45" value={e.duration} onChange={set('duration')} inputRef={durationRef} />
            <UnitField label="Kcal đã đốt" unit="kcal" placeholder="Nếu có" value={e.calories} onChange={set('calories')} approx={!!e.ai} highlight={!!e.ai} />
          </div>
          <AiExplanation text={e.ai?.explanation} />

          <div className="flex flex-col gap-1.5">
            <span className={SMALL_LABEL}>Ảnh {OPTIONAL}</span>
            <div className="flex items-center gap-3">
              <Thumbs files={e.files} size="size-16" onRemove={(j) => update(e.key, { files: e.files.filter((_, k) => k !== j) })} />
              {e.files.length < MAX_WORKOUT_PHOTOS && (
                <label className="flex size-16 shrink-0 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-2xl border border-line bg-base-200/60 text-xs font-semibold focus-within:outline-2 focus-within:outline-primary">
                  <Icon name="photo" className="size-5" /> Thêm ảnh
                  <input type="file" accept="image/*" multiple className="sr-only"
                    onChange={(ev) => { update(e.key, { files: [...e.files, ...Array.from(ev.target.files)].slice(0, MAX_WORKOUT_PHOTOS) }); ev.target.value = ''; }} />
                </label>
              )}
              {e.files.length === 0 && <span className="text-xs text-muted">Ảnh đồng hồ, app chạy bộ… chỉ lưu kèm, không gửi cho AI, không tốn lượt</span>}
            </div>
          </div>
        </div>
      )))}

      <button type="button" className="btn h-11 w-full rounded-[14px] border border-line bg-base-100 text-sm font-semibold" disabled={!!busy || !named(e)} onClick={add}>
        <Icon name="plus" className="size-4" /> Thêm môn khác
      </button>

      {filled.length > 1 && (
        <p className="flex items-baseline justify-between px-1 text-sm">
          <span className="text-muted">{sheet.date === today() ? 'Tổng hôm nay' : 'Tổng'}</span>
          <span className="tabular font-bold">{totalMinutes ? `${totalMinutes} phút · ` : ''}<span className="text-brand-text">+{kcal(totalKcal)} kcal</span></span>
        </p>
      )}

      <ErrorText>{error}</ErrorText>
      <div className="flex flex-col gap-2">
        <button type="button" className={PRIMARY} disabled={!!busy || !ready} onClick={save}>
          {busy === 'save' && <span className="loading loading-spinner loading-sm" />}
          {saveLabel}
        </button>
        {showAi && (
          <button type="button" disabled={!!busy || !toEstimate.length || outOfCredits} onClick={estimate}
            className="btn h-12 w-full rounded-2xl border border-[#17804f] bg-base-100 text-[15px] font-bold text-[#17804f] disabled:border-line">
            {busy === 'analyze' && <><span className="loading loading-spinner loading-sm" /> AI đang ước tính…</>}
            {busy !== 'analyze' && (outOfCredits
              ? 'Hết lượt hôm nay'
              : <><Icon name="sparkles" className="size-4" /> Để AI ước tính kcal <span className="font-semibold">· 1 lượt</span></>)}
          </button>
        )}
        {(showAi || reviewed) && <p className="text-center text-xs text-muted">{footer}</p>}
      </div>
    </div>
  );
}

export function WorkoutSheet({ sheet, onClose, onSaved }) {
  const [reviewing, setReviewing] = useState(false);
  useEffect(() => { setReviewing(false); }, [sheet]);
  const badge = sheet && (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-train-bg px-3 py-1 text-xs font-semibold text-train">
      <Icon name="fire" className="size-3.5" /> {sheet.date === today() ? 'Hôm nay sẽ thành ngày tập' : 'Ngày này sẽ thành ngày tập'}
    </span>
  );
  return (
    <Sheet open={!!sheet} onClose={onClose} title={reviewing ? 'Xem lại trước khi lưu' : 'Ghi tập luyện'} badge={badge}>
      {sheet && <WorkoutForm sheet={sheet} onSaved={onSaved} onReview={setReviewing} />}
    </Sheet>
  );
}
